import { DeleteResponse, HttpService, ObservableCollection, QueryBus, Repository, RepositoryBase, RepositoryResponseCode, RepositorySaveResponse, SaveConflictResponse, SaveForbiddenResponse, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { DiscountKind, OrderDeliveryAddressModel, OrderDeliveryDimensionModel, OrderDeliveryModel, OrderDeliveryShippmentMethodModel, OrderDocumentModel, OrderGetDTO, OrderItemModel, OrderModel, OrderPaymentModel, OrderPaymentSummary, OrderPostDTO, OrderRecipientAddressModel, OrderRecipientModel, OrderSaleDocumentModel } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, pickBy } from '@skylab-shared/utils';
import { AxiosResponse } from 'axios';
import { OrderBO } from './order.bo';

@Repository({
    boType: OrderBO,
    apiController: TaggedUrl.join('orders', [Tags.SelloApi]),
    dto: {
        get: OrderGetDTO,
        update: OrderPostDTO
    },
    customSaveRequestTimeout: 3 * MS_PER_MINUTE
})
export class OrderRepositoryBase extends RepositoryBase<OrderBO, OrderModel, OrderGetDTO, OrderPostDTO> {
    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(http);
    }

    protected createBO(): OrderBO {
        return new OrderBO(this.weaver, this.queryBus);
    }

    protected async fromPlainObjectAsync(data: OrderGetDTO): Promise<OrderModel> {
        const item = data.data.item;
        const model = new OrderModel(pickBy<OrderModel>({
            id: item.orderId,
            orderCustomerLogin: item.customer?.externalNumber,
            comission: item.totalCommission,
            statusId: item.statusId,
            orderDate: new Date(item.orderDate),
            createdDate: new Date(item.createdDate),
            grossValue: item.totalGrossValue,
            netValue: item.totalNetValue,
            dueDate: item.dueDate ? new Date(item.dueDate) : null,
            externalNumber: item.externalNumber,
            number: item.number,
            notesPrivate: item.notesPrivate,
            notesPublic: item.notesPublic,
            source: item.source,
            currencyId: item.currencyExternalId,
            paymentSummary: new OrderPaymentSummary(pickBy<OrderPaymentSummary>({
                isPaid: item.paymentSummary.isPaid,
                paidAmount: item.paymentSummary.paidAmount,
                pendingAmount: item.paymentSummary.pendingAmount,
                totalAmount: item.paymentSummary.totalAmount,
                paymentType: item.paymentSummary.paymentType
            }, val => val !== void 0)),
            payments: new ObservableCollection(item.paymentSummary?.payments?.map(p => {
                return new OrderPaymentModel(pickBy<Partial<OrderPaymentModel>>({
                    amount: p.amount,
                    currencyExternalId: item.currencyExternalId,
                    externalId: p.externalId,
                    finishedAt: p.finishedAt
                        ? new Date(p.finishedAt)
                        : item.orderDate
                            ? new Date(item.orderDate)
                            : new Date(),
                    id: p.id,
                    provider: p.provider,
                }, val => val !== void 0));
            }) || []),
            items: new ObservableCollection(item.items.map(orderItem => {
                return new OrderItemModel(pickBy<Partial<OrderItemModel>>({
                    offerId: orderItem.offerId,
                    offerName: orderItem.offerName,
                    offerUrl: orderItem.offerUrl,
                    commission: orderItem.commission,
                    price_grossBeforeDiscount: orderItem.grossPrice,
                    id: orderItem.id,
                    value_grossBeforeDiscount: orderItem.grossValue,
                    value_netBeforeDiscount: orderItem.netValue,
                    kind: orderItem.kind,
                    name: orderItem.name,
                    price_netBeforeDiscount: orderItem.netPrice,
                    ordinalNumber: orderItem.positionId,
                    quantity: orderItem.quantity,
                    sku: orderItem.sku,
                    vatRateId: orderItem.taxExternalId,
                    unitHeight: orderItem.unitHeight,
                    unitLength: orderItem.unitLength,
                    unitExternalId: orderItem.unitExternalId,
                    unitWeight: orderItem.unitWeight,
                    unitWidth: orderItem.unitWidth,
                    image: orderItem.image,
                    discountKind: DiscountKind.Percentage,
                    discountValue: 0,
                    productId: orderItem.productId,
                    inventoryControl: orderItem.inventoryControl,
                    symbol: orderItem.symbol
                }, val => val !== void 0));
            })),
            delivery: item.delivery ? new OrderDeliveryModel(pickBy<OrderDeliveryModel>({
                firstName: item.delivery.firstName,
                lastName: item.delivery.lastName,
                companyName: item.delivery.companyName,
                address: item.delivery.address ? new OrderDeliveryAddressModel(pickBy<OrderDeliveryAddressModel>({
                    line1: item.delivery.address.line1,
                    line2: item.delivery.address.line2,
                    city: item.delivery.address.city,
                    countryExternalId: item.delivery.address.countryExternalId,
                    zipCode: item.delivery.address.zipCode
                }, val => val !== void 0)) : null,
                dimensions: item.delivery.dimensions ? new OrderDeliveryDimensionModel(pickBy<OrderDeliveryDimensionModel>({
                    height: item.delivery.dimensions.height,
                    weight: item.delivery.dimensions.weight,
                    width: item.delivery.dimensions.width,
                    length: item.delivery.dimensions.length,
                }, val => val !== void 0)) : null,
                dispatchEnd: item.delivery.dispatchEnd ? new Date(item.delivery.dispatchEnd) : null,
                dispatchStart: item.delivery.dispatchStart ? new Date(item.delivery.dispatchStart) : null,
                grossCost: item.delivery.grossCost,
                netCost: item.delivery.netCost,
                parcelSize: item.delivery.parcelSize,
                phone: item.delivery.phone,
                sendingParcelLocker: item.delivery.sendingParcelLocker,
                pickupPointName: item.delivery.pickupPointName,
                taxExternalId: item.delivery.taxExternalId,
                shipmentMethod: item.delivery.shipmentMethod ? new OrderDeliveryShippmentMethodModel(pickBy<OrderDeliveryShippmentMethodModel>({
                    kind: item.delivery.shipmentMethod.kind,
                    serviceId: item.delivery.shipmentMethod.serviceId,
                    serviceName: item.delivery.shipmentMethod.serviceName,
                }, val => val !== void 0)) : new OrderDeliveryShippmentMethodModel(),
            }, val => val !== void 0)) : null,
            document: item.document ? new OrderDocumentModel(pickBy<OrderDocumentModel>({
                documentType: item.document.documentType,
                name: item.document.name,
                taxIdentification: item.document.taxIdentification,
                address: item.document.address ? new OrderRecipientAddressModel(pickBy<OrderRecipientAddressModel>({
                    line1: item.document.address.line1,
                    line2: item.document.address.line2,
                    city: item.document.address.city,
                    countryExternalId: item.document.address.countryExternalId,
                    zipCode: item.document.address.zipCode
                }, val => val !== void 0)) : null
            }, val => val !== void 0)) : null,
            saleDocument: item.saleDocument ? new OrderSaleDocumentModel(pickBy<OrderSaleDocumentModel>({
                kind: item.saleDocument.kind,
                id: item.saleDocument.id,
                saleDocExternalId: item.saleDocument.saleDocExternalId,
                saleDocNumber: item.saleDocument.saleDocNumber
            }, val => val !== void 0)) : null,
            recipient: item.recipient ? new OrderRecipientModel(pickBy<OrderRecipientModel>({
                kind: item.recipient.kind,
                companyName: item.recipient.companyName,
                email: item.recipient.email,
                firstName: item.recipient.firstName,
                lastName: item.recipient.lastName,
                phoneNumber: item.recipient.phoneNumber,
                taxIdentification: item.recipient.taxIdentification,
                address: item.recipient.address ? new OrderRecipientAddressModel(pickBy<OrderRecipientAddressModel>({
                    line1: item.recipient.address.line1,
                    line2: item.recipient.address.line2,
                    city: item.recipient.address.city,
                    countryExternalId: item.recipient.address.countryExternalId,
                    zipCode: item.recipient.address.zipCode
                }, val => val !== void 0)) : null
            }, val => val !== void 0)) : null,
        }, val => val !== undefined));

        return model;
    }

    protected async preparePlainObjectAsync(model: OrderModel): Promise<OrderPostDTO> {
        return {
            orderId: model.id,
            currencyExternalId: model.currencyId,
            statusId: model.statusId,
            commission: model.comission,
            createDate: model.createdDate,
            dueDate: model.dueDate,
            externalNumber: model.externalNumber,
            number: model.number,
            notesPrivate: model.notesPrivate,
            notesPublic: model.notesPublic,
            source: model.source,
            sourceProviderId: model.sourceProviderId,
            delivery: model.delivery ? {
                companyName: model.delivery.companyName,
                address: model.delivery.address ? {
                    city: model.delivery.address.city,
                    line1: model.delivery.address.line1,
                    line2: model.delivery.address.line2,
                    countryExternalId: model.delivery.address.countryExternalId,
                    zipCode: model.delivery.address.zipCode,
                } : null,
                dimensions: model.delivery.dimensions ? {
                    height: model.delivery.dimensions.height,
                    length: model.delivery.dimensions.length,
                    weight: model.delivery.dimensions.weight,
                    width: model.delivery.dimensions.width,
                } : null,
                shipmentMethod: model.delivery.shipmentMethod ? {
                    kind: model.delivery.shipmentMethod.kind,
                    serviceId: model.delivery.shipmentMethod.serviceId,
                    serviceName: model.delivery.shipmentMethod.serviceName,
                } : null,
                firstName: model.delivery.firstName,
                lastName: model.delivery.lastName,
                phone: model.delivery.phone,
                dispatchEnd: model.delivery.dispatchEnd,
                dispatchStart: model.delivery.dispatchStart,
                grossCost: model.delivery.grossCost,
                netCost: model.delivery.netCost,
                parcelSize: model.delivery.parcelSize,
                pickupPointName: model.delivery.pickupPointName,
                sendingParcelLocker: model.delivery.sendingParcelLocker,
                taxExternalId: model.delivery.taxExternalId,
            } : null,
            paymentType: model.paymentSummary.paymentType,
            document: model.document ? {
                address: model.document.address ? {
                    city: model.document.address.city,
                    countryExternalId: model.document.address.countryExternalId,
                    line1: model.document.address.line1,
                    line2: model.document.address.line2,
                    zipCode: model.document.address.zipCode,
                } : null,
                documentType: model.document.documentType,
                name: model.document.name,
                taxIdentification: model.document.taxIdentification,
            } : null,
            items: model.items.map((orderItem) => ({
                commission: orderItem.commission,
                grossPrice: orderItem.price_grossBeforeDiscount,
                id: orderItem.id,
                grossValue: orderItem.value_grossBeforeDiscount,
                kind: orderItem.kind,
                name: orderItem.name,
                netPrice: orderItem.price_netBeforeDiscount,
                netValue: orderItem.value_netBeforeDiscount,
                positionId: orderItem.ordinalNumber,
                quantity: orderItem.quantity,
                sku: orderItem.sku || '',
                taxExternalId: orderItem.vatRateId,
                unitHeight: orderItem.unitHeight,
                unitLength: orderItem.unitLength,
                unitExternalId: orderItem.unitExternalId,
                unitWeight: orderItem.unitWeight,
                unitWidth: orderItem.unitWidth,
                image: orderItem.image,
                productId: orderItem.productId || null,
            })),
            payments: model.payments.map(p => ({
                amount: p.amount,
                currencyExternalId: p.currencyExternalId,
                externalId: p.externalId,
                finishedAt: p.finishedAt,
                id: p.id,
                provider: p.provider
            })),
            orderDate: model.orderDate,
            recipient: model.recipient ? {
                address: model.recipient.address ? {
                    city: model.recipient.address.city,
                    countryExternalId: model.recipient.address.countryExternalId,
                    line1: model.recipient.address.line1,
                    line2: model.recipient.address.line2,
                    zipCode: model.recipient.address.zipCode,
                } : null,
                companyName: model.recipient.companyName,
                email: model.recipient.email,
                firstName: model.recipient.firstName,
                kind: model.recipient.kind,
                lastName: model.recipient.lastName,
                phoneNumber: model.recipient.phoneNumber,
                taxIdentification: model.recipient.taxIdentification
            } : null
        }
    }

    public override async deleteAsync(): Promise<DeleteResponse>{
        throw new Error('Method not allowed');
    }

    protected override prepareSaveResponse(response: AxiosResponse): RepositorySaveResponse | SaveConflictResponse<unknown> | SaveForbiddenResponse<unknown> {
        const code = this.convertResponseToResponseCode(response);

        if (code === RepositoryResponseCode.Success) {
            return { code, object: { id: response?.data?.data?.orderId, version: '', number: response?.data?.data?.number} as any};
        }

        return super.prepareSaveResponse(response);
    }
}
